import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { MovieData } from "./SwipeCard";

interface ShelfRowProps {
  title: string;
  items: MovieData[];
  onItemClick?: (movie: MovieData) => void;
  onTitleClick?: () => void;
  emptyMessage?: string;
}

const ShelfRow = ({ title, items, onItemClick, onTitleClick, emptyMessage = "Nothing here yet" }: ShelfRowProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) el.addEventListener("scroll", checkScroll, { passive: true });
    return () => el?.removeEventListener("scroll", checkScroll);
  }, [items]);

  const scroll = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.7;
    el.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  };

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between px-4 mb-3">
        <h3 className={`font-display text-base font-semibold text-foreground ${onTitleClick ? "cursor-pointer hover:text-primary transition-colors" : ""}`} onClick={onTitleClick}>{title}{onTitleClick ? " →" : ""}</h3>
        {items.length > 0 && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => scroll("left")}
              disabled={!canScrollLeft}
              className={`w-7 h-7 rounded-full flex items-center justify-center transition-all active:scale-90 ${
                canScrollLeft ? "bg-secondary hover:bg-secondary/80 text-foreground" : "opacity-0 pointer-events-none"
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => scroll("right")}
              disabled={!canScrollRight}
              className={`w-7 h-7 rounded-full flex items-center justify-center transition-all active:scale-90 ${
                canScrollRight ? "bg-secondary hover:bg-secondary/80 text-foreground" : "opacity-0 pointer-events-none"
              }`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      {items.length === 0 ? (
        <div className="px-4">
          <div className="rounded-xl bg-secondary/50 border border-border p-6 text-center">
            <p className="text-sm text-muted-foreground">{emptyMessage}</p>
          </div>
        </div>
      ) : (
        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto px-4 pb-2"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
        >
          <style>{`.shelf-scroll::-webkit-scrollbar { display: none; }`}</style>
          {items.map((movie, i) => (
            <motion.button
              key={movie.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              onClick={() => onItemClick?.(movie)}
              className="flex-shrink-0 w-28 group"
            >
              <div className="aspect-[2/3] rounded-lg overflow-hidden border border-border mb-1.5 group-hover:border-primary/50 transition-colors">
                <img src={movie.poster} alt={movie.title} className="w-full h-full object-cover" />
              </div>
              <p className="text-xs text-foreground truncate font-medium">{movie.title}</p>
              <p className="text-[11px] text-muted-foreground">{movie.year}</p>
            </motion.button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ShelfRow;
